import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { coachVoiceLabel } from '@goaltree/backend/convex/lib/coach'
import { USAGE_LIMITS } from '@goaltree/backend/convex/lib/usage'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, usePaginatedQuery } from 'convex/react'
import { ArrowUp, Flag, Loader2, MoreHorizontal, Trash2 } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { CoachAvatar } from '@/components/coach-avatar'
import { MarkdownContent } from '@/components/markdown-content'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useIsPro } from '@/hooks/use-is-pro'
import {
  optimisticDeleteGroveMessage,
  optimisticMarkGroveRead,
  optimisticSendGroveMessage,
} from '@/lib/groves-optimistic'
import { handleUsageLimit } from '@/lib/usage-limit'

import { GROVE_MESSAGE_MAX, toastGroveError } from './grove-utils'

const PAGE = 30
const ARA_TIMEOUT_MS = 45_000

type Message = ReturnType<
  typeof usePaginatedQuery<typeof api.groves.listMessages>
>['results'][number]

function sameDay(a: number, b: number) {
  return new Date(a).toDateString() === new Date(b).toDateString()
}

export function GroveChat({
  groveId,
  isOwner,
  coachVoice,
}: {
  groveId: Id<'groves'>
  isOwner: boolean
  // The grove's coach (the owner's chosen coach).
  coachVoice: string
}) {
  const { t, i18n } = useTranslation()
  const { results, status, loadMore } = usePaginatedQuery(
    api.groves.listMessages,
    { groveId },
    { initialNumItems: PAGE },
  )
  const markRead = useMutation(api.groves.markRead).withOptimisticUpdate(optimisticMarkGroveRead)
  const newestId = results[0]?._id
  const [araSince, setAraSince] = useState<number | null>(null)

  useEffect(() => {
    if (!newestId) return
    const read = () => {
      if (document.visibilityState === 'visible') void markRead({ groveId })
    }
    read()
    document.addEventListener('visibilitychange', read)
    return () => document.removeEventListener('visibilitychange', read)
  }, [newestId, groveId, markRead])

  const araReplied =
    araSince !== null && results.some((m) => m.kind === 'coach' && m.createdAt > araSince)
  useEffect(() => {
    if (araSince === null) return
    if (araReplied) {
      setAraSince(null)
      return
    }
    const id = setTimeout(() => setAraSince(null), ARA_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [araSince, araReplied])

  const topRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = topRef.current
    if (!el || status !== 'CanLoadMore') return
    const io = new IntersectionObserver(([e]) => e.isIntersecting && loadMore(PAGE), {
      rootMargin: '200px',
    })
    io.observe(el)
    return () => io.disconnect()
  }, [status, loadMore])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* column-reverse keeps the view pinned to the newest message as rows arrive */}
      <div className="flex min-h-0 flex-1 flex-col-reverse overflow-y-auto px-4 py-4">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-1">
          <div ref={topRef} />
          {status === 'LoadingFirstPage' ? (
            <ChatSkeleton />
          ) : (
            <>
              {status === 'LoadingMore' && (
                <div className="flex justify-center py-3">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {results.length === 0 && (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {t('groves.chat.empty')}
                </p>
              )}
              {[...results].reverse().map((m, i, arr) => {
                const prev = arr[i - 1]
                const newDay = !prev || !sameDay(prev.createdAt, m.createdAt)
                const grouped =
                  !newDay &&
                  prev.kind === m.kind &&
                  prev.userId === m.userId &&
                  m.createdAt - prev.createdAt < 5 * 60_000
                return (
                  <div key={m._id} className="flex flex-col">
                    {newDay && (
                      <p className="py-3 text-center text-[11px] font-medium text-muted-foreground">
                        {new Date(m.createdAt).toLocaleDateString(i18n.language, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    )}
                    <MessageRow
                      message={m}
                      grouped={grouped}
                      isOwner={isOwner}
                      coachVoice={coachVoice}
                    />
                  </div>
                )
              })}
              {araSince !== null && !araReplied && <AraTyping coachVoice={coachVoice} />}
            </>
          )}
        </div>
      </div>
      <Composer
        groveId={groveId}
        coachVoice={coachVoice}
        onAraReplying={() => setAraSince(Date.now() - 5_000)}
      />
    </div>
  )
}

function MessageRow({
  message: m,
  grouped,
  isOwner,
  coachVoice,
}: {
  message: Message
  grouped: boolean
  isOwner: boolean
  coachVoice: string
}) {
  const { t, i18n } = useTranslation()
  const time = new Date(m.createdAt).toLocaleTimeString(i18n.language, {
    hour: 'numeric',
    minute: '2-digit',
  })

  if (m.kind === 'system')
    return (
      <p className="py-1.5 text-center text-xs text-muted-foreground">
        {t(`groves.system.${m.body}`, { name: m.author?.name ?? '' })}
      </p>
    )

  if (m.kind === 'coach')
    return (
      <div className={cn('flex gap-2', grouped ? 'mt-0.5' : 'mt-3')}>
        <div className="w-[22px] shrink-0">
          {!grouped && (
            <CoachAvatar voice={m.coachVoice ?? coachVoice} size={22} className="mt-0.5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {!grouped && (
            <p className="mb-0.5 text-xs font-bold text-foreground">
              {coachVoiceLabel(m.coachVoice ?? coachVoice)}
            </p>
          )}
          {m.deleted ? <DeletedBody /> : <MarkdownContent text={m.body} className="text-sm" />}
        </div>
        {isOwner && !m.deleted && <MessageMenu message={m} canDelete canReport={false} />}
      </div>
    )

  if (m.isMine)
    return (
      <div className={cn('group flex items-center justify-end gap-1', grouped ? 'mt-0.5' : 'mt-3')}>
        {!m.deleted && <MessageMenu message={m} canDelete canReport={false} />}
        <div
          className={cn(
            'max-w-[80%] rounded-2xl bg-foreground px-3 py-2',
            grouped ? 'rounded-tr-md' : 'rounded-tr-sm',
            m.deleted && 'bg-muted',
          )}
          title={time}
        >
          {m.deleted ? (
            <DeletedBody />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-snug text-background">
              {m.body}
            </p>
          )}
        </div>
      </div>
    )

  return (
    <div className={cn('group flex items-end gap-2', grouped ? 'mt-0.5' : 'mt-3')}>
      <div className="w-7 shrink-0">
        {!grouped && (
          <CanopyAvatar userId={m.userId}>
            <UserAvatar
              name={m.author?.name}
              image={m.author?.image}
              className="size-7 rounded-full object-cover"
            />
          </CanopyAvatar>
        )}
      </div>
      <div className="flex min-w-0 max-w-[80%] flex-col">
        {!grouped && (
          <p className="mb-0.5 px-1 text-xs font-semibold text-muted-foreground">
            {m.author?.name}
          </p>
        )}
        <div
          className={cn(
            'rounded-2xl bg-muted px-3 py-2',
            grouped ? 'rounded-tl-md' : 'rounded-tl-sm',
          )}
          title={time}
        >
          {m.deleted ? (
            <DeletedBody />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-snug text-foreground">
              {m.body}
            </p>
          )}
        </div>
      </div>
      {!m.deleted && <MessageMenu message={m} canDelete={isOwner} canReport />}
    </div>
  )
}

function DeletedBody() {
  const { t } = useTranslation()
  return <p className="text-sm italic text-muted-foreground">{t('groves.chat.deleted')}</p>
}

function MessageMenu({
  message,
  canDelete,
  canReport,
}: {
  message: Message
  canDelete: boolean
  canReport: boolean
}) {
  const { t } = useTranslation()
  const del = useMutation(api.groves.deleteMessage).withOptimisticUpdate(
    optimisticDeleteGroveMessage,
  )
  const report = useMutation(api.groves.reportMessage)
  if (!canDelete && !canReport) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('groves.chat.actions')}
        className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground/50 transition-opacity hover:bg-muted hover:text-foreground aria-expanded:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
      >
        <MoreHorizontal className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44" align="end">
        {canReport && (
          <DropdownMenuItem
            className="cursor-pointer px-3 font-medium"
            onClick={() =>
              report({ messageId: message._id })
                .then(() => toast.success(t('groves.chat.reported')))
                .catch((err) => toastGroveError(err, t))
            }
          >
            <Flag className="text-muted-foreground" />
            <span>{t('groves.chat.report')}</span>
          </DropdownMenuItem>
        )}
        {canDelete && (
          <DropdownMenuItem
            variant="destructive"
            className="cursor-pointer px-3 font-medium"
            onClick={() => del({ messageId: message._id }).catch((err) => toastGroveError(err, t))}
          >
            <Trash2 />
            <span>{t('common.delete')}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function AraTyping({ coachVoice }: { coachVoice: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-3 flex gap-2"
    >
      <CoachAvatar voice={coachVoice} size={22} speaking className="mt-0.5" />
      <div className="flex items-center gap-1 rounded-2xl bg-muted px-3 py-2.5">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="size-1.5 rounded-full bg-muted-foreground"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </motion.div>
  )
}

function Composer({
  groveId,
  coachVoice,
  onAraReplying,
}: {
  groveId: Id<'groves'>
  coachVoice: string
  onAraReplying: () => void
}) {
  const coach = coachVoiceLabel(coachVoice)
  const { t } = useTranslation()
  const send = useMutation(api.groves.sendMessage).withOptimisticUpdate(optimisticSendGroveMessage)
  const { tier } = useIsPro()
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  function resize() {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }

  async function handleSend() {
    const body = input.trim()
    if (!body || sending) return
    setSending(true)
    setInput('')
    requestAnimationFrame(resize)
    try {
      const { ara } = await send({ groveId, body, tzOffset: new Date().getTimezoneOffset() })
      if (ara === 'replying') onAraReplying()
      else if (ara === 'upgrade') setUpgradeOpen(true)
      else if (ara === 'limit' && tier !== 'free')
        handleUsageLimit({
          data: {
            kind: 'usageLimit',
            feature: 'coachChat',
            plan: tier,
            limit: USAGE_LIMITS.coachChat[tier].limit,
          },
        })
    } catch (err) {
      setInput(body)
      toastGroveError(err, t)
    } finally {
      setSending(false)
      ref.current?.focus()
    }
  }

  function mentionAra() {
    setInput((s) => (s.toLowerCase().includes(`@${coach.toLowerCase()}`) ? s : `@${coach} ${s}`))
    ref.current?.focus()
  }

  return (
    <div className="shrink-0 border-t border-border/50 bg-background px-4 pt-2.5 pb-3 max-lg:pb-[calc(64px+max(env(safe-area-inset-bottom),10px)+0.75rem)]">
      <div className="mx-auto max-w-2xl" data-keep-centered>
        <div className="flex items-end gap-1.5 rounded-3xl border border-border bg-card py-1 pr-1 pl-1.5 focus-within:border-primary/45">
          <button
            type="button"
            onClick={mentionAra}
            aria-label={t('groves.chat.askAra', { coach })}
            title={t('groves.chat.askAra', { coach })}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted"
          >
            <CoachAvatar voice={coachVoice} size={24} />
          </button>
          <textarea
            ref={ref}
            value={input}
            maxLength={GROVE_MESSAGE_MAX}
            onChange={(e) => {
              setInput(e.target.value)
              resize()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void handleSend()
              }
            }}
            rows={1}
            placeholder={t('groves.chat.placeholder', { coach })}
            className="min-h-9 flex-1 resize-none overflow-hidden bg-transparent py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            aria-label={t('groves.chat.send')}
            className={cn(
              'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-default',
              input.trim() && !sending
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {sending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ArrowUp className="size-4" strokeWidth={2.25} />
            )}
          </button>
        </div>
      </div>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        reason="coach"
        title={t('groves.chat.araUpgradeTitle', { coach })}
        description={t('groves.chat.araUpgradeDesc', { coach })}
      />
    </div>
  )
}

function ChatSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[
        { mine: false, w: 160 },
        { mine: false, w: 220 },
        { mine: true, w: 140 },
        { mine: false, w: 190 },
        { mine: true, w: 200 },
      ].map((b, i) => (
        <div key={i} className={cn('flex items-end gap-2', b.mine && 'justify-end')}>
          {!b.mine && <div className="size-7 shrink-0 animate-pulse rounded-full bg-muted/30" />}
          <div className="h-9 animate-pulse rounded-2xl bg-muted/30" style={{ width: b.w }} />
        </div>
      ))}
    </div>
  )
}
